import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiRequestError } from '@/lib/apiClient';

export interface ApiQueryState<T> {
  data: T | null;
  isLoading: boolean;
  error: ApiRequestError | null;
  /** Re-runs the request — for a retry button or after a mutation. */
  refetch: () => void;
}

/**
 * Runs a request and tracks loading / error / data for it.
 *
 * Every screen needs the same four states (CLAUDE.md §6) and writing them by
 * hand in each page is how they end up inconsistent. `deps` works like
 * `useEffect`'s: change a filter or a page number and the request re-runs.
 *
 * A result that arrives after the inputs changed is discarded, so a slow page 1
 * can never overwrite a fast page 2.
 */
export function useApiQuery<T>(fetcher: () => Promise<T>, deps: unknown[]): ApiQueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ApiRequestError | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Identifies the newest request, so stale responses can be ignored.
  const requestId = useRef(0);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    const currentRequest = ++requestId.current;
    let isActive = true;

    setIsLoading(true);
    setError(null);

    fetcher()
      .then((result) => {
        if (!isActive || currentRequest !== requestId.current) return;
        setData(result);
      })
      .catch((caught: unknown) => {
        if (!isActive || currentRequest !== requestId.current) return;
        setError(
          caught instanceof ApiRequestError
            ? caught
            : new ApiRequestError('Something went wrong. Please try again.', 0, 'INTERNAL_ERROR'),
        );
      })
      .finally(() => {
        if (!isActive || currentRequest !== requestId.current) return;
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
    // The fetcher closure changes on every render; `deps` is the real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadToken]);

  return { data, isLoading, error, refetch };
}
